import { useState, useEffect, useRef } from "react";
import { motion, useInView, AnimatePresence } from "framer-motion";
import {
  Truck, Wrench, Fuel, ClipboardCheck, ShieldCheck, MapPin,
  FileText, Users, Building2, Bell, BarChart3,
  Clock, FileSpreadsheet, Smartphone, ChevronDown,
  ArrowRight, Check, X, Award, Frown, ChevronLeft, ChevronRight
} from "lucide-react";
import { createClient } from "@supabase/supabase-js";

// ── LIGAÇÃO A DADOS (definida nas variáveis de ambiente do site) ───────────
const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL || "";
const SUPABASE_ANON = process.env.REACT_APP_SUPABASE_ANON || "";
const supabase = SUPABASE_URL ? createClient(SUPABASE_URL, SUPABASE_ANON) : null;

// ── TEMA (consistente com a aplicação RUMO) ─────────────────────────────────
const C = {
  bg: "#FFFFFF",
  bgSoft: "#F8FAFC",
  bgDark: "#122457",
  bgDarkSoft: "#1B316E",
  primary: "#2563EB",
  primaryDark: "#1D4ED8",
  primaryLight: "#EFF6FF",
  text: "#0F172A",
  textMuted: "#475569",
  border: "#E2E8F0",
  white: "#FFFFFF",
  onDark: "#FFFFFF",
  onDarkMuted: "#B9C6E8",
  onDarkFaint: "#93C5FD",
  orange: "#EA580C",
  emerald: "#059669",
  red: "#DC2626",
};

// ── HELPERS ───────────────────────────────────────────────────────────────────
function useScrollReveal(threshold = 0.15) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, amount: threshold });
  return { ref, inView };
}

const fadeUp = {
  hidden: { opacity: 0, y: 40 },
  visible: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.65, delay: i * 0.08, ease: [0.22, 1, 0.36, 1] } }),
};

const stagger = { visible: { transition: { staggerChildren: 0.07 } } };

// ── DATA ──────────────────────────────────────────────────────────────────────
const PAIN_POINTS = [
  { icon: FileSpreadsheet, text: "Horas perdidas a atualizar folhas de Excel" },
  { icon: Clock, text: "Inspeções e seguros que expiram sem aviso" },
  { icon: Frown, text: "Avarias sem registo nem histórico" },
  { icon: BarChart3, text: "Custos da frota impossíveis de analisar" },
  { icon: Smartphone, text: "A equipa no terreno sem acesso a nada" },
  { icon: Building2, text: "Viaturas espalhadas por obras sem controlo" },
];

const MODULES = [
  { icon: Truck, color: C.primary, title: "Frota", desc: "Ficha completa de cada viatura ou máquina: dados técnicos, financeiros e de propriedade, tudo num só lugar.", tags: ["Viaturas", "Máquinas", "Histórico"] },
  { icon: Fuel, color: C.orange, title: "Combustível", desc: "Abastecimentos externos e internos, stock de depósito e consumo médio por viatura, obra ou condutor. Importa o extrato do cartão frota e a plataforma resolve sozinha a viatura, o condutor e o tipo de combustível de cada linha.", tags: ["Abastecimentos", "Importação de Extratos", "Depósitos"] },
  { icon: Wrench, color: C.emerald, title: "Manutenção", desc: "Ordens de serviço e histórico de intervenções. A próxima revisão nunca mais passa despercebida.", tags: ["Ordens de Serviço", "Custos", "Histórico"] },
  { icon: ClipboardCheck, color: C.red, title: "Inspeções", desc: "Registo de inspeções periódicas com checklist e resultado, com alerta automático antes da validade terminar.", tags: ["Checklist", "Alertas", "Validades"] },
  { icon: ShieldCheck, color: C.primary, title: "Seguros", desc: "Apólices por viatura, documento anexado e alerta automático de renovação. Sem sobressaltos de última hora.", tags: ["Apólices", "Renovações", "Documentos"] },
  { icon: MapPin, color: C.orange, title: "Portagens", desc: "Registo manual de passagens ou importação em massa do extrato Via Verde, com deteção automática de colunas e associação de cada passagem à viatura certa.", tags: ["Via Verde", "Importação", "Por Obra"] },
  { icon: FileText, color: C.emerald, title: "Documentos", desc: "Repositório documental por viatura, com controlo de validade e visibilidade por perfil de utilizador.", tags: ["Repositório", "Validades", "Perfis"] },
  { icon: Users, color: C.red, title: "Equipa", desc: "Gestão de condutores, histórico de atribuição de viaturas e utilizadores com acesso à plataforma.", tags: ["Condutores", "Atribuições", "Utilizadores"] },
  { icon: Building2, color: C.primary, title: "Obras / Centros", desc: "Aloca viaturas a obras ou centros de custo. Sabes sempre quanto custa cada obra e o que tem alocado.", tags: ["Alocação", "Custos", "Histórico"] },
];

const BENEFITS = [
  { num: "Toda a frota", label: "num único painel", desc: "Estado de cada viatura e ativo, sempre atualizado e visível de imediato." },
  { num: "Zero", label: "surpresas", desc: "Alertas automáticos antes de qualquer inspeção, seguro ou documento vencer." },
  { num: "Sem papel", label: "nem folhas soltas", desc: "Registos e documentos centralizados, acessíveis de qualquer lugar." },
  { num: "Em segundos", label: "no registo de campo", desc: "O condutor regista pelo telemóvel. O gestor vê tudo em tempo real." },
];

const SPOTLIGHT = [
  { icon: Bell, color: C.orange, title: "Alertas antes que seja tarde", desc: "Inspeções, seguros e documentos a vencer geram um alerta automático, com a antecedência suficiente para agir." },
  { icon: Smartphone, color: C.emerald, title: "Acesso direto do telemóvel", desc: "Cada condutor tem o seu próprio acesso, sem instalar nada. Basta abrir no browser do telemóvel." },
  { icon: BarChart3, color: C.primary, title: "Relatórios prontos a exportar", desc: "Excel detalhado por viatura, obra ou período. Quando alguém pede os números, já os tens." },
  { icon: Award, color: C.emerald, title: "Histórico permanente por ativo", desc: "Cada viatura guarda o seu próprio histórico: manutenções, custos, avarias e condutores, sempre consultável." },
  { icon: FileSpreadsheet, color: C.orange, title: "Importa cartões frota e Via Verde em massa", desc: "Carrega o extrato do fornecedor e a plataforma associa sozinha cada linha à viatura, ao condutor e à obra certa, sem tocares numa folha de Excel." },
  { icon: ShieldCheck, color: C.primary, title: "Nada entra na frota sem confirmares", desc: "Cada linha importada fica em revisão até seres tu a confirmá-la. Duplicados, taxas e linhas de subtotal são identificados e nunca contaminam os teus dados." },
];

const FAQS = [
  { q: "Quanto tempo leva a começar a usar a plataforma?", a: "O arranque é acompanhado pela nossa equipa. Marcamos uma sessão convosco, configuramos a plataforma à vossa realidade e importamos os dados que já existem (Excel, por exemplo) sem custo adicional. Normalmente fica tudo a funcionar na primeira semana." },
  { q: "Serve só para viaturas ou também para máquinas?", a: "Serve para viaturas ligeiras, pesadas, máquinas e qualquer ativo que precise de controlo por quilómetros ou horas de utilização." },
  { q: "É preciso instalar alguma aplicação no telemóvel?", a: "Não. Cada condutor acede à sua área diretamente a partir do browser do telemóvel, sem instalar nada. Funciona em qualquer smartphone com ligação à internet, e está incluído em todos os planos." },
  { q: "Os meus dados ficam seguros?", a: "Sim. Cada empresa vê apenas os seus próprios dados, alojados em infraestrutura cloud fiável, com cópias de segurança automáticas e ligação encriptada em todas as comunicações." },
  { q: "Posso cancelar quando quiser?", a: "Sim. Sem contratos longos. Cancela quando quiseres e exportas todos os teus dados." },
];

const PLANS = [
  { name: "Frota 5",   range: "até 5",   price: "14,99", perAtivo: "≈ 3,00€/ativo",  featured: false },
  { name: "Frota 10",  range: "6 a 10",  price: "24,99", perAtivo: "≈ 2,50€/ativo",  featured: false },
  { name: "Frota 15",  range: "11 a 15", price: "34,99", perAtivo: "≈ 2,33€/ativo",  featured: true  },
  { name: "Frota 20",  range: "16 a 20", price: "44,99", perAtivo: "≈ 2,25€/ativo",  featured: false },
  { name: "Frota 25",  range: "21 a 25", price: "54,99", perAtivo: "≈ 2,20€/ativo",  featured: false },
  { name: "Frota 30",  range: "26 a 30", price: "64,99", perAtivo: "≈ 2,17€/ativo",  featured: false },
  { name: "Frota 30+", range: "30+",     price: null,    perAtivo: "Preço negociado", featured: false },
];

// Screenshots da galeria — substitui os ficheiros em public/screenshots
// mantendo os mesmos nomes (ver public/screenshots/LEIA-ME.txt)
const SCREENSHOTS = [
  { src: "/screenshots/dashboard.png", label: "Dashboard" },
  { src: "/screenshots/frota.png", label: "Frota" },
  { src: "/screenshots/combustivel.png", label: "Combustível" },
  { src: "/screenshots/manutencao.png", label: "Manutenção" },
  { src: "/screenshots/obras.png", label: "Obras / Centros" },
  { src: "/screenshots/mobile.png", label: "App no Telemóvel" },
];

// ── COMPONENTS ────────────────────────────────────────────────────────────────

function Navbar({ onContact }) {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  useEffect(() => {
    const fn = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", fn);
    return () => window.removeEventListener("scroll", fn);
  }, []);
  const links = [["#modulos", "Módulos"], ["#video", "Apresentação"], ["#galeria", "Galeria"], ["#precos", "Preços"], ["#faq", "FAQ"]];
  return (
    <>
      <nav style={{
        position: "fixed", top: 0, left: 0, right: 0, zIndex: 200,
        padding: "0 clamp(1.25rem, 4vw, 4rem)",
        height: "68px",
        display: "flex", alignItems: "center", justifyContent: "space-between",
        background: (scrolled || menuOpen) ? "rgba(255,255,255,0.92)" : "transparent",
        backdropFilter: (scrolled || menuOpen) ? "blur(16px)" : "none",
        borderBottom: (scrolled || menuOpen) ? `1px solid ${C.border}` : "1px solid transparent",
        transition: "all 0.3s",
      }}>
        <a href="#top" style={{ display: "flex", alignItems: "center", gap: "0.5rem", fontWeight: 900, fontSize: "1.4rem", color: C.text, textDecoration: "none" }}>
          <span style={{ width: 34, height: 34, borderRadius: 10, background: C.primary, display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontSize: "1.05rem", fontWeight: 900 }}>R</span>
          RUMO
        </a>

        <div className="nav-links" style={{ display: "flex", gap: "2.5rem" }}>
          {links.map(([href, label]) => (
            <a key={href} href={href} style={{ color: C.textMuted, textDecoration: "none", fontSize: "0.86rem", fontWeight: 600, transition: "color 0.2s" }}
              onMouseEnter={e => e.target.style.color = C.text}
              onMouseLeave={e => e.target.style.color = C.textMuted}>{label}</a>
          ))}
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: "0.75rem" }}>
          <motion.button whileHover={{ scale: 1.04 }} whileTap={{ scale: 0.97 }}
            onClick={onContact} className="nav-cta-desktop"
            style={{ background: C.primary, color: "#fff", border: "none", padding: "0.65rem 1.6rem", borderRadius: "50px", fontWeight: 700, fontSize: "0.86rem", cursor: "pointer" }}>
            Pedir Demonstração
          </motion.button>
          <button className="hamburger" onClick={() => setMenuOpen(p => !p)}
            style={{ background: "none", border: `1px solid ${C.border}`, borderRadius: 8, padding: "0.4rem 0.5rem", cursor: "pointer", display: "flex", flexDirection: "column", gap: "4px" }}>
            {[0,1,2].map(i => (
              <span key={i} style={{ display: "block", width: 20, height: 2, background: C.text, borderRadius: 2,
                transform: menuOpen ? (i===0 ? "rotate(45deg) translate(4px,4px)" : i===2 ? "rotate(-45deg) translate(4px,-4px)" : "scaleX(0)") : "none",
                transition: "all 0.2s" }} />
            ))}
          </button>
        </div>
      </nav>

      {/* Mobile menu drawer */}
      <AnimatePresence>
        {menuOpen && (
          <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }}
            style={{ position: "fixed", top: 68, left: 0, right: 0, zIndex: 199, background: "rgba(255,255,255,0.98)", backdropFilter: "blur(16px)", borderBottom: `1px solid ${C.border}`, padding: "1.5rem clamp(1.25rem,4vw,4rem) 2rem" }}>
            <div style={{ display: "flex", flexDirection: "column", gap: "0" }}>
              {links.map(([href, label]) => (
                <a key={href} href={href} onClick={() => setMenuOpen(false)}
                  style={{ color: C.text, textDecoration: "none", fontSize: "1.05rem", fontWeight: 600, padding: "1rem 0", borderBottom: `1px solid ${C.border}` }}>
                  {label}
                </a>
              ))}
              <motion.button whileTap={{ scale: 0.97 }} onClick={() => { setMenuOpen(false); onContact(); }}
                style={{ marginTop: "1.25rem", background: C.primary, color: "#fff", border: "none", padding: "0.9rem", borderRadius: 50, fontWeight: 700, fontSize: "1rem", cursor: "pointer" }}>
                Pedir Demonstração
              </motion.button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}

function HeroSection({ onContact }) {
  const words = ["Excel", "confusão", "papel", "atrasos", "stress"];
  const [wi, setWi] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setWi(p => (p + 1) % words.length), 2200);
    return () => clearInterval(t);
  }, [words.length]);

  return (
    <section style={{
      minHeight: "92vh", display: "flex", flexDirection: "column",
      alignItems: "center", justifyContent: "center", textAlign: "center",
      padding: "9rem 1.5rem 5rem", position: "relative", overflow: "hidden",
      background: `radial-gradient(ellipse at 50% -10%, ${C.bgDarkSoft} 0%, ${C.bgDark} 55%, #0B1533 100%)`,
      color: C.onDark,
    }}>
      {/* Decorative blobs (mesmo motivo visual usado na apresentação) */}
      <div style={{ position: "absolute", inset: 0, pointerEvents: "none", overflow: "hidden" }}>
        <div style={{ position: "absolute", top: "-12%", right: "-8%", width: 420, height: 420, borderRadius: "50%", background: "#1E40AF", opacity: 0.55 }} />
        <div style={{ position: "absolute", bottom: "-15%", left: "-10%", width: 360, height: 360, borderRadius: "50%", background: "#1E40AF", opacity: 0.4 }} />
      </div>

      <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}
        style={{ display: "inline-flex", alignItems: "center", gap: "0.5rem", background: "rgba(255,255,255,0.08)", border: "1px solid rgba(255,255,255,0.18)", color: C.onDarkFaint, fontSize: "0.72rem", fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", padding: "0.45rem 1.2rem", borderRadius: "50px", marginBottom: "2rem", position: "relative" }}>
        <span style={{ width: 6, height: 6, borderRadius: "50%", background: C.onDarkFaint, animation: "pulse 1.5s infinite" }} />
        Onde a operação ganha direção
      </motion.div>

      <motion.h1 initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3, duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
        style={{ fontWeight: 800, fontSize: "clamp(2.6rem, 6.5vw, 4.6rem)", lineHeight: 1.08, letterSpacing: "-0.02em", marginBottom: "1.5rem", position: "relative" }}>
        <div>Chega de</div>
        <div style={{ position: "relative", display: "inline-block", color: C.onDarkFaint, minWidth: "6ch" }}>
          <AnimatePresence mode="wait">
            <motion.span key={wi} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }} style={{ display: "block" }}>
              {words[wi]}
            </motion.span>
          </AnimatePresence>
        </div>
      </motion.h1>

      <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.5 }}
        style={{ maxWidth: 580, fontSize: "1.12rem", color: C.onDarkMuted, lineHeight: 1.75, marginBottom: "2.5rem", position: "relative" }}>
        Uma plataforma que reúne, num único lugar, toda a informação da frota. <strong style={{ color: "#fff", fontWeight: 700 }}>Viaturas, manutenção, combustível, documentos e alertas</strong> - sempre atualizados, em qualquer dispositivo.
      </motion.p>

      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.6 }}
        style={{ display: "flex", gap: "1rem", flexWrap: "wrap", justifyContent: "center", position: "relative" }}>
        <motion.button whileHover={{ scale: 1.04, boxShadow: "0 16px 48px rgba(37,99,235,0.5)" }} whileTap={{ scale: 0.97 }}
          onClick={onContact}
          style={{ background: C.primary, color: "#fff", border: "none", padding: "1rem 2.4rem", borderRadius: "50px", fontSize: "1rem", fontWeight: 700, cursor: "pointer", display: "flex", alignItems: "center", gap: "0.5rem" }}>
          Pedir Demonstração Gratuita <ArrowRight size={16} />
        </motion.button>
        <motion.a whileHover={{ scale: 1.02 }} href="#modulos"
          style={{ background: "transparent", color: "#fff", border: "1px solid rgba(255,255,255,0.25)", padding: "1rem 2.4rem", borderRadius: "50px", fontSize: "1rem", fontWeight: 500, cursor: "pointer", textDecoration: "none", display: "flex", alignItems: "center", gap: "0.5rem" }}>
          Ver funcionalidades <ChevronDown size={16} />
        </motion.a>
      </motion.div>

      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.9 }}
        style={{ display: "flex", gap: "2.5rem", marginTop: "4.5rem", flexWrap: "wrap", justifyContent: "center", position: "relative" }}>
        {["Sem Excel", "Sem contratos longos", "Arranque acompanhado", "Acesso incluído para toda a equipa"].map(t => (
          <span key={t} style={{ fontSize: "0.82rem", color: C.onDarkMuted, fontWeight: 500, display: "flex", alignItems: "center", gap: "0.4rem" }}>
            <Check size={14} color={C.onDarkFaint} /> {t}
          </span>
        ))}
      </motion.div>
    </section>
  );
}

function PainSection() {
  const { ref, inView } = useScrollReveal();
  return (
    <section ref={ref} style={{ padding: "6rem clamp(1.5rem, 5vw, 4rem)", background: C.bg }}>
      <div style={{ maxWidth: 1100, margin: "0 auto" }}>
        <motion.div initial="hidden" animate={inView ? "visible" : "hidden"} variants={stagger}>
          <motion.p variants={fadeUp} style={{ fontSize: "0.78rem", fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", color: C.primary, marginBottom: "0.75rem", textAlign: "center" }}>
            A gestão de frota não devia ser assim
          </motion.p>
          <motion.h2 variants={fadeUp} style={{ fontWeight: 800, fontSize: "clamp(2rem, 4.5vw, 3rem)", letterSpacing: "-0.01em", textAlign: "center", marginBottom: "0.75rem", color: C.text }}>
            Reconheces alguma destas situações?
          </motion.h2>
          <motion.p variants={fadeUp} style={{ color: C.textMuted, textAlign: "center", maxWidth: 520, margin: "0 auto 3.5rem", lineHeight: 1.7 }}>
            Muitas empresas ainda gerem a frota assim. E depois pagam o preço em multas, avarias e horas perdidas.
          </motion.p>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: "1rem" }}>
            {PAIN_POINTS.map(({ icon: Icon, text }, i) => (
              <motion.div key={text} custom={i} variants={fadeUp}
                style={{ display: "flex", alignItems: "center", gap: "1rem", padding: "1.25rem 1.5rem", background: "#FEF2F2", border: "1px solid #FEE2E2", borderRadius: 16 }}>
                <div style={{ width: 40, height: 40, borderRadius: 10, background: C.red, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                  <Icon size={18} color="#fff" />
                </div>
                <span style={{ fontSize: "0.92rem", color: C.text, fontWeight: 500 }}>{text}</span>
              </motion.div>
            ))}
          </div>
          <motion.div variants={fadeUp} style={{ textAlign: "center", marginTop: "3rem" }}>
            <div style={{ display: "inline-block", padding: "1.2rem 2.5rem", background: C.primaryLight, border: "1px solid #BFDBFE", borderRadius: 16, color: C.primaryDark, fontSize: "1rem", fontWeight: 500, lineHeight: 1.6 }}>
              A RUMO existe <strong style={{ color: C.text }}>para resolver exatamente isto</strong>.
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}

function ModulesSection() {
  const { ref, inView } = useScrollReveal();
  return (
    <section id="modulos" ref={ref} style={{ padding: "7rem clamp(1.5rem, 5vw, 4rem)", background: C.bgSoft }}>
      <div style={{ maxWidth: 1100, margin: "0 auto" }}>
        <motion.div initial="hidden" animate={inView ? "visible" : "hidden"} variants={stagger}>
          <motion.p variants={fadeUp} style={{ fontSize: "0.78rem", fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", color: C.primary, marginBottom: "0.75rem" }}>
            Plataforma completa
          </motion.p>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", marginBottom: "3.5rem", flexWrap: "wrap", gap: "1rem" }}>
            <motion.h2 variants={fadeUp} style={{ fontWeight: 800, fontSize: "clamp(2rem, 4.5vw, 3rem)", letterSpacing: "-0.01em", color: C.text }}>
              9 módulos.<br />Uma só plataforma.
            </motion.h2>
            <motion.p variants={fadeUp} style={{ color: C.textMuted, maxWidth: 380, lineHeight: 1.7, fontSize: "0.95rem" }}>
              Cada módulo resolve um problema real da tua operação. Sem complexidade. Sem curva de aprendizagem longa.
            </motion.p>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))", gap: "1.25rem" }}>
            {MODULES.map(({ icon: Icon, color, title, desc, tags }, i) => (
              <motion.div key={title} custom={i} variants={fadeUp}
                whileHover={{ y: -4, boxShadow: "0 12px 32px rgba(15,23,42,0.08)" }}
                style={{ background: C.white, border: `1px solid ${C.border}`, borderRadius: 20, padding: "1.75rem", cursor: "default", transition: "box-shadow 0.3s" }}>
                <div style={{ width: 50, height: 50, borderRadius: 14, background: color, display: "flex", alignItems: "center", justifyContent: "center", marginBottom: "1.1rem" }}>
                  <Icon size={22} color="#fff" />
                </div>
                <h3 style={{ fontSize: "1.05rem", fontWeight: 700, marginBottom: "0.65rem", color: C.text }}>{title}</h3>
                <p style={{ fontSize: "0.88rem", color: C.textMuted, lineHeight: 1.65, marginBottom: "1.1rem" }}>{desc}</p>
                <div style={{ display: "flex", flexWrap: "wrap", gap: "0.4rem" }}>
                  {tags.map(t => (
                    <span key={t} style={{ fontSize: "0.7rem", fontWeight: 600, padding: "0.25rem 0.7rem", borderRadius: 50, background: C.bgSoft, color: C.textMuted, border: `1px solid ${C.border}` }}>{t}</span>
                  ))}
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
}

function BenefitsSection() {
  const { ref, inView } = useScrollReveal();
  return (
    <section id="vantagens" ref={ref} style={{ padding: "7rem clamp(1.5rem, 5vw, 4rem)", background: `linear-gradient(160deg, ${C.bgDark} 0%, #0B1533 100%)`, position: "relative", overflow: "hidden", color: C.onDark }}>
      <div style={{ position: "absolute", top: "-10%", right: "-6%", width: 460, height: 460, borderRadius: "50%", background: "#1E40AF", opacity: 0.35, pointerEvents: "none" }} />

      <div style={{ maxWidth: 1100, margin: "0 auto", position: "relative" }}>
        <motion.div initial="hidden" animate={inView ? "visible" : "hidden"} variants={stagger}>
          <motion.p variants={fadeUp} style={{ fontSize: "0.78rem", fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", color: C.onDarkFaint, marginBottom: "0.75rem", textAlign: "center" }}>Porquê a RUMO</motion.p>
          <motion.h2 variants={fadeUp} style={{ fontWeight: 800, fontSize: "clamp(2rem, 4.5vw, 3rem)", letterSpacing: "-0.01em", textAlign: "center", marginBottom: "4rem" }}>
            O que muda no dia a dia
          </motion.h2>

          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: "1.5rem", marginBottom: "5rem" }}>
            {BENEFITS.map(({ num, label, desc }, i) => (
              <motion.div key={label} custom={i} variants={fadeUp}
                style={{ padding: "2rem 1.75rem", background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.12)", borderRadius: 20 }}
                whileHover={{ borderColor: "rgba(147,197,253,0.4)", scale: 1.02 }}>
                <div style={{ fontWeight: 800, fontSize: "1.5rem", color: C.onDarkFaint, letterSpacing: "-0.01em", lineHeight: 1.2, marginBottom: "0.35rem" }}>{num}</div>
                <h4 style={{ fontSize: "0.95rem", fontWeight: 700, marginBottom: "0.5rem", color: "#fff" }}>{label}</h4>
                <p style={{ fontSize: "0.84rem", color: C.onDarkMuted, lineHeight: 1.6 }}>{desc}</p>
              </motion.div>
            ))}
          </div>

          {/* Feature spotlight */}
          <motion.div variants={fadeUp} style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))", gap: "1.25rem" }}>
            {SPOTLIGHT.map(({ icon: Icon, color, title, desc }) => (
              <div key={title} style={{ display: "flex", gap: "1rem", padding: "1.5rem", background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 16 }}>
                <div style={{ width: 44, height: 44, borderRadius: 12, background: color, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                  <Icon size={20} color="#fff" />
                </div>
                <div>
                  <h4 style={{ fontSize: "0.92rem", fontWeight: 700, marginBottom: "0.4rem", color: "#fff" }}>{title}</h4>
                  <p style={{ fontSize: "0.83rem", color: C.onDarkMuted, lineHeight: 1.6 }}>{desc}</p>
                </div>
              </div>
            ))}
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}

function VideoSection() {
  const { ref, inView } = useScrollReveal();
  return (
    <section id="video" ref={ref} style={{ padding: "6rem clamp(1.5rem, 5vw, 4rem)", background: C.bg }}>
      <div style={{ maxWidth: 980, margin: "0 auto" }}>
        <motion.div initial="hidden" animate={inView ? "visible" : "hidden"} variants={stagger}>
          <motion.p variants={fadeUp} style={{ fontSize: "0.78rem", fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", color: C.primary, marginBottom: "0.75rem", textAlign: "center" }}>
            Apresentação
          </motion.p>
          <motion.h2 variants={fadeUp} style={{ fontWeight: 800, fontSize: "clamp(2rem, 4.5vw, 3rem)", letterSpacing: "-0.01em", textAlign: "center", marginBottom: "0.75rem", color: C.text }}>
            Conhece a RUMO em 2 minutos
          </motion.h2>
          <motion.p variants={fadeUp} style={{ color: C.textMuted, textAlign: "center", maxWidth: 520, margin: "0 auto 2.5rem", lineHeight: 1.7 }}>
            Um resumo rápido do que a plataforma oferece, dos módulos aos preços.
          </motion.p>

          <motion.div variants={fadeUp} style={{ borderRadius: 24, overflow: "hidden", border: `1px solid ${C.border}`, boxShadow: "0 24px 60px rgba(15,23,42,0.12)", background: C.bgDark }}>
            <video
              controls
              playsInline
              preload="metadata"
              poster="/media/rumo-demo-poster.jpg"
              style={{ width: "100%", display: "block", aspectRatio: "16/9", background: C.bgDark }}
            >
              <source src="/media/rumo-demo.mp4" type="video/mp4" />
              O teu browser não suporta vídeo incorporado.
            </video>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}

function GallerySection() {
  const { ref, inView } = useScrollReveal();
  const [openIndex, setOpenIndex] = useState(null);

  const showPrev = () => setOpenIndex(i => (i - 1 + SCREENSHOTS.length) % SCREENSHOTS.length);
  const showNext = () => setOpenIndex(i => (i + 1) % SCREENSHOTS.length);

  useEffect(() => {
    if (openIndex === null) return;
    const onKey = (e) => {
      if (e.key === "Escape") setOpenIndex(null);
      if (e.key === "ArrowLeft") showPrev();
      if (e.key === "ArrowRight") showNext();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openIndex]);

  return (
    <section id="galeria" ref={ref} style={{ padding: "6rem clamp(1.5rem, 5vw, 4rem)", background: C.bgSoft }}>
      <div style={{ maxWidth: 1100, margin: "0 auto" }}>
        <motion.div initial="hidden" animate={inView ? "visible" : "hidden"} variants={stagger}>
          <motion.p variants={fadeUp} style={{ fontSize: "0.78rem", fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", color: C.primary, marginBottom: "0.75rem", textAlign: "center" }}>
            Galeria
          </motion.p>
          <motion.h2 variants={fadeUp} style={{ fontWeight: 800, fontSize: "clamp(2rem, 4.5vw, 3rem)", letterSpacing: "-0.01em", textAlign: "center", marginBottom: "0.75rem", color: C.text }}>
            Screenshots da plataforma
          </motion.h2>
          <motion.p variants={fadeUp} style={{ color: C.textMuted, textAlign: "center", maxWidth: 520, margin: "0 auto 3rem", lineHeight: 1.7 }}>
            Clica numa imagem para veres em detalhe.
          </motion.p>

          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: "1.25rem" }}>
            {SCREENSHOTS.map(({ src, label }, i) => (
              <motion.button key={label} custom={i} variants={fadeUp}
                onClick={() => setOpenIndex(i)}
                whileHover={{ y: -4, boxShadow: "0 12px 32px rgba(15,23,42,0.12)" }}
                style={{ padding: 0, border: `1px solid ${C.border}`, borderRadius: 16, overflow: "hidden", background: C.white, cursor: "pointer", textAlign: "left", transition: "box-shadow 0.3s" }}>
                <div style={{ width: "100%", aspectRatio: "16/10", overflow: "hidden", background: C.bgSoft }}>
                  <img src={src} alt={label} style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} />
                </div>
                <div style={{ padding: "0.9rem 1.1rem" }}>
                  <span style={{ fontSize: "0.9rem", fontWeight: 700, color: C.text }}>{label}</span>
                </div>
              </motion.button>
            ))}
          </div>
        </motion.div>
      </div>

      {/* Lightbox */}
      <AnimatePresence>
        {openIndex !== null && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            onClick={() => setOpenIndex(null)}
            style={{ position: "fixed", inset: 0, background: "rgba(15,23,42,0.85)", backdropFilter: "blur(6px)", zIndex: 300, display: "flex", alignItems: "center", justifyContent: "center", padding: "2rem" }}>

            <button onClick={() => setOpenIndex(null)}
              style={{ position: "absolute", top: "1.5rem", right: "1.5rem", background: "rgba(255,255,255,0.12)", border: "none", borderRadius: 8, width: 40, height: 40, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", color: "#fff" }}>
              <X size={20} />
            </button>

            <button onClick={(e) => { e.stopPropagation(); showPrev(); }}
              style={{ position: "absolute", left: "1.5rem", top: "50%", transform: "translateY(-50%)", background: "rgba(255,255,255,0.12)", border: "none", borderRadius: "50%", width: 44, height: 44, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", color: "#fff" }}>
              <ChevronLeft size={22} />
            </button>
            <button onClick={(e) => { e.stopPropagation(); showNext(); }}
              style={{ position: "absolute", right: "1.5rem", top: "50%", transform: "translateY(-50%)", background: "rgba(255,255,255,0.12)", border: "none", borderRadius: "50%", width: 44, height: 44, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", color: "#fff" }}>
              <ChevronRight size={22} />
            </button>

            <motion.div key={openIndex} initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.95 }}
              onClick={e => e.stopPropagation()}
              style={{ maxWidth: "90vw", maxHeight: "80vh", display: "flex", flexDirection: "column", alignItems: "center", gap: "1rem" }}>
              <img src={SCREENSHOTS[openIndex].src} alt={SCREENSHOTS[openIndex].label}
                style={{ maxWidth: "100%", maxHeight: "70vh", borderRadius: 12, boxShadow: "0 24px 60px rgba(0,0,0,0.5)" }} />
              <span style={{ color: "#fff", fontWeight: 600, fontSize: "0.95rem" }}>{SCREENSHOTS[openIndex].label}</span>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}

function PricingSection({ onContact }) {
  const { ref, inView } = useScrollReveal();
  const [anual, setAnual] = useState(false);

  const getAnualPrice = (monthly) => {
    if (!monthly) return null;
    return (parseFloat(monthly.replace(",", ".")) * 11).toFixed(2).replace(".", ",");
  };

  const FEATURES_ALL = [
    "Todos os módulos incluídos",
    "Acesso incluído para toda a equipa, do telemóvel",
    "Alertas automáticos",
    "Exportação para Excel",
    "Arranque acompanhado",
    "Importação dos dados existentes incluída",
  ];

  return (
    <section id="precos" ref={ref} style={{ padding: "7rem clamp(1.5rem, 5vw, 4rem)", background: C.bg }}>
      <div style={{ maxWidth: 860, margin: "0 auto" }}>
        <motion.div initial="hidden" animate={inView ? "visible" : "hidden"} variants={stagger}>

          <motion.p variants={fadeUp} style={{ fontSize: "0.78rem", fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", color: C.primary, marginBottom: "0.75rem", textAlign: "center" }}>Preços</motion.p>
          <motion.h2 variants={fadeUp} style={{ fontWeight: 800, fontSize: "clamp(2rem, 4.5vw, 3rem)", letterSpacing: "-0.01em", textAlign: "center", marginBottom: "0.75rem", color: C.text }}>
            Simples. Transparente. Sem surpresas.
          </motion.h2>
          <motion.p variants={fadeUp} style={{ color: C.textMuted, textAlign: "center", marginBottom: "2.5rem", lineHeight: 1.65 }}>
            Sem taxas escondidas. Cancelas quando quiseres. Os teus dados são sempre teus.
          </motion.p>

          {/* Toggle mensal / anual */}
          <motion.div variants={fadeUp} style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "1rem", marginBottom: "3rem" }}>
            <span style={{ fontSize: "0.88rem", fontWeight: 600, color: !anual ? C.text : "#94A3B8", transition: "color 0.2s" }}>Mensal</span>
            <button onClick={() => setAnual(p => !p)}
              style={{ width: 52, height: 28, borderRadius: 50, border: "none", cursor: "pointer", position: "relative", background: anual ? C.primary : "#E2E8F0", transition: "background 0.3s", flexShrink: 0 }}>
              <span style={{ position: "absolute", top: 3, left: anual ? 27 : 3, width: 22, height: 22, borderRadius: "50%", background: "#fff", transition: "left 0.25s", display: "block", boxShadow: "0 1px 3px rgba(0,0,0,0.25)" }} />
            </button>
            <span style={{ fontSize: "0.88rem", fontWeight: 600, color: anual ? C.text : "#94A3B8", transition: "color 0.2s", display: "flex", alignItems: "center", gap: "0.5rem" }}>
              Anual
              <AnimatePresence>
                {anual && (
                  <motion.span initial={{ opacity: 0, scale: 0.7 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.7 }}
                    style={{ background: C.primary, color: "#fff", fontSize: "0.62rem", fontWeight: 800, letterSpacing: "0.08em", padding: "0.2rem 0.65rem", borderRadius: 50 }}>
                    1 MÊS GRÁTIS
                  </motion.span>
                )}
              </AnimatePresence>
            </span>
          </motion.div>

          {/* Tabela */}
          <motion.div variants={fadeUp} style={{ borderRadius: 20, border: `1px solid ${C.border}`, overflow: "hidden" }}>

            {/* Header */}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", background: C.text, padding: "1rem 1.5rem" }}>
              <div style={{ fontSize: "0.72rem", fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", color: "#fff" }}>Plano</div>
              <div style={{ fontSize: "0.72rem", fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", color: "#CBD5E1", textAlign: "center" }}>Ativos</div>
              <div style={{ fontSize: "0.72rem", fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", color: "#CBD5E1", textAlign: "center" }}>Preço</div>
              <div style={{ fontSize: "0.72rem", fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", color: "#CBD5E1", textAlign: "center" }}>€/ativo</div>
            </div>

            {/* Rows */}
            {PLANS.map(({ name, range, price, perAtivo, featured }, i) => {
              const displayPrice = anual && price ? getAnualPrice(price) : price;
              const suffix = price === null ? "" : anual ? "/ano" : "/mês";
              return (
                <motion.div key={name}
                  whileHover={{ background: featured ? C.primaryLight : C.bgSoft }}
                  style={{
                    display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr",
                    padding: "1.1rem 1.5rem", alignItems: "center",
                    borderBottom: i < PLANS.length - 1 ? `1px solid ${C.border}` : "none",
                    background: featured ? C.primaryLight : (i % 2 === 0 ? C.white : C.bgSoft),
                    transition: "background 0.2s", cursor: "default",
                    position: "relative",
                  }}>
                  {featured && (
                    <div style={{ position: "absolute", left: 0, top: 0, bottom: 0, width: 4, background: C.primary }} />
                  )}

                  {/* Nome */}
                  <div style={{ display: "flex", alignItems: "center", gap: "0.6rem", flexWrap: "wrap" }}>
                    <span style={{ fontWeight: 700, fontSize: "0.95rem", color: C.text }}>{name}</span>
                    {featured && (
                      <span style={{ background: C.primary, color: "#fff", fontSize: "0.55rem", fontWeight: 800, letterSpacing: "0.08em", padding: "0.15rem 0.55rem", borderRadius: 50 }}>POPULAR</span>
                    )}
                  </div>

                  {/* Ativos */}
                  <div style={{ textAlign: "center", fontSize: "0.88rem", color: C.textMuted, fontWeight: 500 }}>{range}</div>

                  {/* Preço */}
                  <div style={{ textAlign: "center" }}>
                    {price === null ? (
                      <span style={{ fontSize: "0.88rem", color: C.primary, fontWeight: 700 }}>Sob consulta</span>
                    ) : (
                      <span>
                        <AnimatePresence mode="wait">
                          <motion.span key={anual ? "a" : "m"} initial={{ opacity: 0, y: -6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 6 }} transition={{ duration: 0.18 }}
                            style={{ fontWeight: 800, fontSize: "1.3rem", color: featured ? C.primary : C.text }}>
                            {displayPrice}€
                          </motion.span>
                        </AnimatePresence>
                        <span style={{ fontSize: "0.72rem", color: "#94A3B8", marginLeft: "0.2rem" }}>{suffix}</span>
                      </span>
                    )}
                  </div>

                  {/* €/ativo */}
                  <div style={{ textAlign: "center" }}>
                    <span style={{ fontSize: "0.75rem", fontWeight: 600, padding: "0.2rem 0.65rem", borderRadius: 50, background: "#ECFDF5", border: "1px solid #A7F3D0", color: C.emerald }}>{perAtivo}</span>
                  </div>
                </motion.div>
              );
            })}
          </motion.div>

          {/* O que está incluído em todos os planos */}
          <motion.div variants={fadeUp} style={{ marginTop: "2.5rem", padding: "1.75rem 2rem", background: C.bgSoft, border: `1px solid ${C.border}`, borderRadius: 16 }}>
            <p style={{ fontSize: "0.72rem", fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", color: "#94A3B8", marginBottom: "1rem" }}>Incluído em todos os planos</p>
            <div style={{ display: "flex", flexWrap: "wrap", gap: "0.75rem" }}>
              {FEATURES_ALL.map(f => (
                <div key={f} style={{ display: "flex", alignItems: "center", gap: "0.45rem", fontSize: "0.85rem", color: C.text }}>
                  <Check size={13} color={C.primary} strokeWidth={2.5} />
                  {f}
                </div>
              ))}
            </div>
          </motion.div>

          {/* CTA */}
          <motion.div variants={fadeUp} style={{ textAlign: "center", marginTop: "2.5rem" }}>
            <motion.button whileHover={{ scale: 1.04, boxShadow: "0 16px 48px rgba(37,99,235,0.35)" }} whileTap={{ scale: 0.97 }}
              onClick={onContact}
              style={{ background: C.primary, color: "#fff", border: "none", padding: "1rem 2.8rem", borderRadius: 50, fontSize: "1rem", fontWeight: 700, cursor: "pointer", display: "inline-flex", alignItems: "center", gap: "0.5rem" }}>
              Pedir Demonstração Gratuita <ArrowRight size={16} />
            </motion.button>
            <p style={{ marginTop: "1rem", fontSize: "0.8rem", color: "#94A3B8" }}>Sem compromisso. Sem cartão de crédito.</p>
          </motion.div>

        </motion.div>
      </div>
    </section>
  );
}

function FaqSection() {
  const { ref, inView } = useScrollReveal();
  const [open, setOpen] = useState(null);
  return (
    <section id="faq" ref={ref} style={{ padding: "7rem clamp(1.5rem, 5vw, 4rem)", background: C.bgSoft }}>
      <div style={{ maxWidth: 760, margin: "0 auto" }}>
        <motion.div initial="hidden" animate={inView ? "visible" : "hidden"} variants={stagger}>
          <motion.p variants={fadeUp} style={{ fontSize: "0.78rem", fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", color: C.primary, marginBottom: "0.75rem", textAlign: "center" }}>FAQ</motion.p>
          <motion.h2 variants={fadeUp} style={{ fontWeight: 800, fontSize: "clamp(2rem, 4.5vw, 3rem)", letterSpacing: "-0.01em", textAlign: "center", marginBottom: "3.5rem", color: C.text }}>Perguntas frequentes</motion.h2>
          {FAQS.map(({ q, a }, i) => (
            <motion.div key={q} custom={i} variants={fadeUp}
              style={{ background: C.white, marginBottom: "0.75rem", borderRadius: 14, border: `1px solid ${C.border}`, overflow: "hidden" }}>
              <button onClick={() => setOpen(open === i ? null : i)}
                style={{ width: "100%", background: "none", border: "none", padding: "1.25rem 1.5rem", display: "flex", justifyContent: "space-between", alignItems: "center", cursor: "pointer", color: C.text, textAlign: "left" }}>
                <span style={{ fontSize: "0.97rem", fontWeight: 600 }}>{q}</span>
                <motion.span animate={{ rotate: open === i ? 45 : 0 }} transition={{ duration: 0.2 }}
                  style={{ color: C.primary, fontSize: "1.4rem", fontWeight: 300, lineHeight: 1, flexShrink: 0, marginLeft: "1rem" }}>+</motion.span>
              </button>
              <AnimatePresence>
                {open === i && (
                  <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }} style={{ overflow: "hidden" }}>
                    <p style={{ padding: "0 1.5rem 1.4rem", fontSize: "0.9rem", color: C.textMuted, lineHeight: 1.7 }}>{a}</p>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}

function ContactModal({ open, onClose }) {
  const [form, setForm] = useState({ nome: "", empresa: "", email: "", telefone: "", frota: "", mensagem: "" });
  const [honeypot, setHoneypot] = useState("");
  const [status, setStatus] = useState("idle"); // idle | loading | success | error | rate_limited | bot
  const lastSubmitRef = useRef(0);

  useEffect(() => {
    if (open) {
      setForm({ nome: "", empresa: "", email: "", telefone: "", frota: "", mensagem: "" });
      setHoneypot("");
      setStatus("idle");
      lastSubmitRef.current = 0;
    }
  }, [open]);

  const handle = (e) => setForm(p => ({ ...p, [e.target.name]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.nome || !form.email) return;

    if (honeypot) {
      setStatus("bot");
      return;
    }

    const now = Date.now();
    if (now - lastSubmitRef.current < 60000 && lastSubmitRef.current !== 0) {
      setStatus("rate_limited");
      return;
    }

    setStatus("loading");
    lastSubmitRef.current = now;

    try {
      if (supabase) {
        const { error } = await supabase.from("rumo_leads").insert([{
          nome: form.nome.trim(),
          empresa: form.empresa.trim(),
          email: form.email.trim().toLowerCase(),
          telefone: form.telefone.trim(),
          dimensao_frota: form.frota,
          mensagem: form.mensagem.trim(),
          origem: window.location.href,
          user_agent: navigator.userAgent.slice(0, 200),
        }]);
        if (error) throw error;
      } else {
        await new Promise(r => setTimeout(r, 1000));
      }
      setStatus("success");
    } catch (err) {
      console.error(err);
      setStatus("error");
    }
  };

  const inputStyle = { width: "100%", padding: "0.75rem 1rem", background: C.bgSoft, border: `1px solid ${C.border}`, borderRadius: 10, color: C.text, fontSize: "0.9rem", fontFamily: "inherit", outline: "none" };
  const labelStyle = { fontSize: "0.78rem", fontWeight: 600, color: C.textMuted, marginBottom: "0.4rem", display: "block" };

  return (
    <AnimatePresence>
      {open && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          onClick={onClose}
          style={{ position: "fixed", inset: 0, background: "rgba(15,23,42,0.55)", backdropFilter: "blur(6px)", zIndex: 300, display: "flex", alignItems: "center", justifyContent: "center", padding: "1rem", overflowY: "auto" }}>
          <motion.div initial={{ opacity: 0, scale: 0.93, y: 30 }} animate={{ opacity: 1, scale: 1, y: 0 }} exit={{ opacity: 0, scale: 0.93, y: 20 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            onClick={e => e.stopPropagation()}
            style={{ background: C.white, border: `1px solid ${C.border}`, borderRadius: 24, padding: "2rem 1.5rem 2.5rem", width: "100%", maxWidth: 520, maxHeight: "92dvh", overflowY: "auto", overscrollBehavior: "contain", position: "relative", WebkitOverflowScrolling: "touch", boxShadow: "0 24px 60px rgba(15,23,42,0.25)" }}>

            <button onClick={onClose} style={{ position: "absolute", top: "1.25rem", right: "1.25rem", background: C.bgSoft, border: "none", borderRadius: 8, width: 32, height: 32, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", color: C.textMuted }}>
              <X size={16} />
            </button>

            {status === "success" ? (
              <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} style={{ textAlign: "center", padding: "2rem 0" }}>
                <div style={{ width: 64, height: 64, borderRadius: "50%", background: "#ECFDF5", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 1.5rem" }}>
                  <Check size={28} color={C.emerald} />
                </div>
                <h3 style={{ fontWeight: 800, fontSize: "1.6rem", marginBottom: "0.75rem", color: C.text }}>Recebido!</h3>
                <p style={{ color: C.textMuted, lineHeight: 1.65 }}>Entraremos em contacto contigo em menos de 24 horas para agendar a tua demonstração personalizada.</p>
                <motion.button whileHover={{ scale: 1.03 }} onClick={onClose}
                  style={{ marginTop: "2rem", background: C.primary, color: "#fff", border: "none", padding: "0.85rem 2.5rem", borderRadius: 50, fontWeight: 700, cursor: "pointer", fontSize: "0.9rem" }}>
                  Fechar
                </motion.button>
              </motion.div>
            ) : (
              <>
                <div style={{ marginBottom: "1.75rem" }}>
                  <p style={{ fontSize: "0.72rem", fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", color: C.primary, marginBottom: "0.5rem" }}>Demonstração gratuita</p>
                  <h3 style={{ fontWeight: 800, fontSize: "1.6rem", marginBottom: "0.5rem", color: C.text }}>Fala connosco</h3>
                  <p style={{ fontSize: "0.87rem", color: C.textMuted, lineHeight: 1.6 }}>Respondemos em menos de 24h e marcamos uma demonstração personalizada para a tua frota.</p>
                </div>

                <form onSubmit={submit} style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
                  <div style={{ position: "absolute", left: "-9999px", opacity: 0, height: 0, overflow: "hidden" }} aria-hidden="true">
                    <input name="website" value={honeypot} onChange={e => setHoneypot(e.target.value)} tabIndex={-1} autoComplete="off" />
                  </div>
                  <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: "1rem" }}>
                    <div><label style={labelStyle}>Nome *</label><input name="nome" value={form.nome} onChange={handle} required placeholder="João Silva" style={inputStyle} /></div>
                    <div><label style={labelStyle}>Empresa</label><input name="empresa" value={form.empresa} onChange={handle} placeholder="Empresa Lda." style={inputStyle} /></div>
                  </div>
                  <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: "1rem" }}>
                    <div><label style={labelStyle}>Email *</label><input name="email" type="email" value={form.email} onChange={handle} required placeholder="joao@empresa.pt" style={inputStyle} /></div>
                    <div><label style={labelStyle}>Telefone</label><input name="telefone" value={form.telefone} onChange={handle} placeholder="+351 9xx xxx xxx" style={inputStyle} /></div>
                  </div>
                  <div>
                    <label style={labelStyle}>Dimensão da frota</label>
                    <select name="frota" value={form.frota} onChange={handle} style={{ ...inputStyle, cursor: "pointer" }}>
                      <option value="">Seleciona...</option>
                      <option value="1-5">até 5 ativos</option>
                      <option value="6-10">6 a 10 ativos</option>
                      <option value="11-15">11 a 15 ativos</option>
                      <option value="16-20">16 a 20 ativos</option>
                      <option value="21-25">21 a 25 ativos</option>
                      <option value="26-30">26 a 30 ativos</option>
                      <option value="30+">Mais de 30</option>
                    </select>
                  </div>
                  <div>
                    <label style={labelStyle}>Mensagem</label>
                    <textarea name="mensagem" value={form.mensagem} onChange={handle} placeholder="Conta-nos o teu desafio atual..." rows={3}
                      style={{ ...inputStyle, resize: "vertical", minHeight: 80 }} />
                  </div>
                  <motion.button whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }} type="submit"
                    disabled={status === "loading"}
                    style={{ background: C.primary, color: "#fff", border: "none", padding: "1rem", borderRadius: 50, fontWeight: 700, fontSize: "0.95rem", cursor: status === "loading" ? "not-allowed" : "pointer", marginTop: "0.25rem", display: "flex", alignItems: "center", justifyContent: "center", gap: "0.5rem", opacity: status === "loading" ? 0.6 : 1, transition: "opacity 0.2s" }}>
                    {status === "loading"
                      ? <><span style={{ width: 16, height: 16, border: "2px solid rgba(255,255,255,0.4)", borderTopColor: "#fff", borderRadius: "50%", display: "inline-block", animation: "spin 0.7s linear infinite" }} /> A enviar...</>
                      : <><span>Quero a minha demonstração gratuita</span> <ArrowRight size={16} /></>}
                  </motion.button>
                  {status === "error" && (
                    <p style={{ color: C.red, fontSize: "0.83rem", textAlign: "center", padding: "0.5rem", background: "#FEF2F2", borderRadius: 8 }}>
                      Algo correu mal. Tenta novamente ou envia um email para <strong>[o teu email de contacto]</strong>
                    </p>
                  )}
                  {status === "rate_limited" && (
                    <p style={{ color: C.orange, fontSize: "0.83rem", textAlign: "center", padding: "0.5rem", background: "#FFF7ED", borderRadius: 8 }}>
                      Aguarda um momento antes de submeter novamente.
                    </p>
                  )}
                </form>
              </>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

function CtaSection({ onContact }) {
  const { ref, inView } = useScrollReveal();
  return (
    <section ref={ref} style={{ padding: "8rem clamp(1.5rem, 5vw, 4rem)", textAlign: "center", position: "relative", overflow: "hidden", background: `linear-gradient(160deg, ${C.bgDark} 0%, #0B1533 100%)`, color: C.onDark }}>
      <div style={{ position: "absolute", bottom: "-15%", left: "50%", transform: "translateX(-50%)", width: 500, height: 300, borderRadius: "50%", background: "#1E40AF", opacity: 0.35, pointerEvents: "none" }} />
      <motion.div initial="hidden" animate={inView ? "visible" : "hidden"} variants={stagger} style={{ position: "relative" }}>
        <motion.p variants={fadeUp} style={{ fontSize: "0.78rem", fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", color: C.onDarkFaint, marginBottom: "1.25rem" }}>Pronto para começar?</motion.p>
        <motion.h2 variants={fadeUp} style={{ fontWeight: 800, fontSize: "clamp(2.2rem, 6vw, 4rem)", lineHeight: 1.15, letterSpacing: "-0.01em", marginBottom: "1.5rem" }}>
          <div>Vamos falar sobre</div>
          <div style={{ color: C.onDarkFaint }}>a tua frota?</div>
        </motion.h2>
        <motion.p variants={fadeUp} style={{ color: C.onDarkMuted, maxWidth: 480, margin: "0 auto 2.5rem", fontSize: "1.05rem", lineHeight: 1.7 }}>
          Arranque acompanhado pela nossa equipa. Sem contratos longos. Sem surpresas. Com o teu histórico existente migrado sem custo.
        </motion.p>
        <motion.div variants={fadeUp} style={{ display: "flex", gap: "1rem", justifyContent: "center", flexWrap: "wrap" }}>
          <motion.button whileHover={{ scale: 1.04, boxShadow: "0 20px 60px rgba(37,99,235,0.5)" }} whileTap={{ scale: 0.97 }}
            onClick={onContact}
            style={{ background: C.primary, color: "#fff", border: "none", padding: "1.1rem 2.8rem", borderRadius: 50, fontSize: "1.05rem", fontWeight: 700, cursor: "pointer", display: "flex", alignItems: "center", gap: "0.5rem" }}>
            Pedir Demonstração Gratuita <ArrowRight size={16} />
          </motion.button>
          <a href="mailto:geral@rumo-frota.pt" style={{ background: "transparent", color: "#fff", border: "1px solid rgba(255,255,255,0.28)", padding: "1.1rem 2.8rem", borderRadius: 50, fontSize: "1.05rem", fontWeight: 500, textDecoration: "none", display: "flex", alignItems: "center", gap: "0.5rem" }}>
            geral@rumo-frota.pt
          </a>
        </motion.div>
      </motion.div>
    </section>
  );
}

// ── APP ───────────────────────────────────────────────────────────────────────
export default function App() {
  const [contactOpen, setContactOpen] = useState(false);

  return (
    <div style={{ background: C.bg, color: C.text, fontFamily: "'Inter', sans-serif", minHeight: "100vh" }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800;900&display=swap');
        *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
        html { scroll-behavior: smooth; }
        body { overflow-x: hidden; }
        @keyframes pulse { 0%,100%{opacity:1} 50%{opacity:0.2} }
        @keyframes spin { to { transform: rotate(360deg); } }
        input:focus, textarea:focus, select:focus { border-color: rgba(37,99,235,0.5) !important; box-shadow: 0 0 0 3px rgba(37,99,235,0.12); }
        ::-webkit-scrollbar { width: 6px; } ::-webkit-scrollbar-track { background: #F8FAFC; } ::-webkit-scrollbar-thumb { background: #CBD5E1; border-radius: 3px; }
        .nav-links { display: flex; }
        .nav-cta-desktop { display: inline-flex; }
        .hamburger { display: none !important; }
        @media(max-width:768px){
          .nav-links { display: none !important; }
          .nav-cta-desktop { display: none !important; }
          .hamburger { display: flex !important; }
        }
      `}</style>

      <Navbar onContact={() => setContactOpen(true)} />
      <HeroSection onContact={() => setContactOpen(true)} />
      <VideoSection />
      <PainSection />
      <ModulesSection />
      <GallerySection />
      <BenefitsSection />
      <PricingSection onContact={() => setContactOpen(true)} />
      <FaqSection />
      <CtaSection onContact={() => setContactOpen(true)} />

      <footer style={{ background: C.text, padding: "2.5rem clamp(1.5rem,5vw,4rem)", display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: "1.5rem" }}>
        <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", fontWeight: 900, fontSize: "1.2rem", color: "#fff" }}>
          <span style={{ width: 28, height: 28, borderRadius: 8, background: C.primary, display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontSize: "0.9rem", fontWeight: 900 }}>R</span>
          RUMO
        </div>
        <p style={{ color: "#94A3B8", fontSize: "0.82rem" }}>© 2025 RUMO · Onde a operação ganha direção · Portugal</p>
        <div style={{ display: "flex", gap: "1.5rem" }}>
          {["Privacidade", "Termos", "Contacto"].map(l => (
            <a key={l} href={l === "Contacto" ? "mailto:geral@rumo-frota.pt" : l === "Privacidade" ? "/privacidade" : "/termos"} style={{ color: "#94A3B8", textDecoration: "none", fontSize: "0.82rem" }}>{l}</a>
          ))}
        </div>
      </footer>

      <ContactModal open={contactOpen} onClose={() => setContactOpen(false)} />
    </div>
  );
}
